import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@stores/auth";
import { authApi } from "@api/modules/auth.api";
import { APP_ROLE } from "@lib/authKeys";

interface RequireAuthProps {
    children: React.ReactNode;
}

export const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
    const location = useLocation();
    const token = useAuthStore((state) => state.token);
    const user = useAuthStore((state) => state.user);
    const revalidated = useRef(false);
    const [, setChecked] = useState(false);

    // Deliberately NOT comparing the JWT `exp` with the device clock: a phone with a skewed clock
    // would treat a fresh token as expired and bounce straight back to /login. The server is the
    // authority — an expired/revoked token gets a 401, which the http client turns into a
    // refresh-or-clearAuth.
    const valid = Boolean(token) && user?.role === APP_ROLE;

    useEffect(() => {
        if (!valid || revalidated.current) {
            return;
        }
        revalidated.current = true;
        let active = true;
        authApi
            .me()
            .then(({ user: fresh }) => {
                if (!active) {
                    return;
                }
                if (fresh?.role === APP_ROLE) {
                    useAuthStore.getState().updateUser(fresh);
                } else {
                    useAuthStore.getState().clearAuth();
                }
            })
            .catch(() => {
                /* 401 is handled by the response interceptor -> clearAuth */
            })
            .finally(() => {
                if (active) {
                    setChecked(true);
                }
            });
        return () => {
            active = false;
        };
    }, [valid]);

    if (!valid) {
        return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }

    return <>{children}</>;
};
